import React, { ReactNode, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { LoadingOutlined } from "@ant-design/icons";
import { ClassNames, useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import { useLocation } from "wouter";

import { CreditAccessWrapper } from "components/paywall/CreditAccessWrapper";
import Button from "components/ui/button/Button";
import { EllipsisTooltipText } from "lib/core_components/EllipsisTooltip";
import { useUploadBidDocuments } from "lib/hooks/api/bid/bid_document/useUploadBidDocuments";
import { OpportunityDetails, useOpportunity } from "lib/hooks/api/opportunities/useOpportunity";
import { useRestricedViewNotice } from "lib/hooks/useRestrictedRowClick";
import { IconKey, UIcon } from "lib/icons/untitled_ui/UIcon";
import { routes } from "lib/routes";
import { EventNames, useTracking } from "lib/tracking";
import { NoticesStagesv2 } from "lib/types/graphQLEnums";
import { tenderStages, TenderStageV2Groups } from "lib/types/models";
import { useCreateBid } from "../../lib/hooks/api/bid/bids/useCreateBid";
import { Flex, Text } from "../../styles/utility-components";
import { DetailsContent, DetailsPage } from "../app_layout/DetailsLayout";
import UploadDocumentsForm from "./UploadDocumentsForm";

type BidDocumentUploadForm = {
  files: File[];
};

type Props = {
  opportunityId: string;
};
export function BidDocumentUploadPage({ opportunityId }: Props) {
  const { logEvent } = useTracking();
  const [, setLocation] = useLocation();
  const [bidId, setBidId] = useState<string | undefined>(undefined);
  const [filesProcessing, setFilesProcessing] = useState<boolean>(false);

  const { colors } = useTheme();

  // TODO: Maybe create a check which ensures that a user isn't on this page if
  //  a bid has already been created for this opportunity
  const { data: opportunity, isLoading: opportunityLoading } = useOpportunity(opportunityId);

  // TODO: In time we will remove. We should be solely hinging bids off
  // opportunities in future. This is the procurement stage id that will be
  // used to create the bid. The logic for this is described in BID-265
  const associatedNotice: OpportunityDetails["notices"][0] | undefined = useMemo(() => {
    if (!opportunity?.notices || opportunity.notices.length === 0) {
      return;
    }
    // If only 1 notice, take this notice
    if (opportunity.notices.length === 1) {
      return opportunity.notices[0];
    }

    // Sort notices ascending (most recently published first)
    const sortedNotices = opportunity.notices.sort((a, b) => {
      const dateA = a.publishDate ? new Date(a.publishDate).getTime() : Infinity;
      const dateB = b.publishDate ? new Date(b.publishDate).getTime() : Infinity;
      return dateB - dateA;
    });

    // Get all tenders
    const tenders = sortedNotices.filter(
      (notice) =>
        tenderStages[notice.stagev2 ?? NoticesStagesv2.UnknownTender].group ===
        TenderStageV2Groups.TENDER,
    );

    // Get all open tenders from tenders
    const openTenders = tenders.filter((tender) => tender.stagev2 === NoticesStagesv2.OpenTender);

    if (tenders.length > 0) {
      // If multiple open tenders, take the most recently published tender
      if (openTenders.length > 0) {
        return openTenders[0];
      } else {
        // Otherwise just return the most recently published tender
        return tenders[0];
      }
    }

    // Otherwise just return the most recently published notice
    return sortedNotices[0];
  }, [opportunity]);

  const noticeId = associatedNotice?.guid;
  const procurementStageId = associatedNotice?.procurementStage.id;

  const { control, watch } = useForm<BidDocumentUploadForm>({
    defaultValues: { files: [] },
    mode: "onChange",
  });

  const { files } = watch();

  const onFinishUploadingFiles = (bidId: string) => {
    logEvent(EventNames.qualificationReportCreated, {
      "Action type": "Button clicked",
      "Number of documents": files.length,
      "Opportunity GUID": opportunityId,
      "Opportunity name": opportunity?.name,
    });

    setLocation(`/${opportunityId}/bid_qualification/${bidId}`);
  };

  const {
    mutateAsync: uploadFiles,
    isLoading: isUploadingFiles,
    uploadStatus,
    setUploadStatus,
  } = useUploadBidDocuments({
    files,
    bidId,
    setFilesProcessing,
    onComplete: onFinishUploadingFiles,
  });

  const { mutateAsync: createBid, isLoading: isCreatingBid } = useCreateBid({
    onSuccess: async (bid) => {
      setBidId(bid.createBid.id);
      await uploadFiles({
        bidId: bid.createBid.id,
      });
    },
  });

  const { viewRecord } = useRestricedViewNotice("See tender notice click");

  const handleNoticeClick = (id: string) => {
    if (window.currentUser) {
      viewRecord(id);
    } else {
      window.open(`${routes.records}/${id}`, "_blank");
    }
  };

  const isUploadingBid = isCreatingBid || isUploadingFiles || filesProcessing;

  return (
    <DetailsPage>
      {opportunity && (
        <BreadcrumbContainer>
          <Flex alignItems="center" gap={8}>
            <Breadcrumb onClick={() => setLocation("/")}>
              <UIcon icon="target04" size={14} color={colors.sysPrimaryDefault} />
              Opportunities
            </Breadcrumb>
            <Text color={colors.sysTextSecondary}> / </Text>
            <Breadcrumb onClick={() => setLocation(`/${opportunityId}`)}>
              <UIcon icon="target04" size={14} color={colors.sysPrimaryDefault} />
              <ClassNames>
                {({ css }) => (
                  <EllipsisTooltipText
                    fullText={opportunity?.name}
                    containerClassname={css({
                      display: "-webkit-box",
                      WebkitLineClamp: 1,
                      lineClamp: 1,
                      WebkitBoxOrient: "vertical",
                      maxWidth: 250,
                    })}
                  />
                )}
              </ClassNames>
            </Breadcrumb>
            <Text color={colors.sysTextSecondary}> / </Text>
            <Breadcrumb inactive>
              <UIcon icon="clipboardCheck" size={14} color={colors.sysTextSecondary} />
              Bid / no-bid report
            </Breadcrumb>
          </Flex>
        </BreadcrumbContainer>
      )}
      <DetailsContent>
        <form>
          <Container>
            <Flex gap={32} flexDirection="column" width={328}>
              <Text h1>Upload tender documents</Text>
              <Flex flexDirection="column" gap={24}>
                <Step
                  stepNo={1}
                  icon="linkExternal02"
                  description="Download the tender documents published by the buyer."
                  cta={
                    noticeId && (
                      <StyledLinkText onClick={() => handleNoticeClick(noticeId)}>
                        See tender notice
                      </StyledLinkText>
                    )
                  }
                />
                <Step stepNo={2} icon="upload01" description="Upload the documents here." />
                <Step
                  stepNo={3}
                  icon="stars2"
                  description="We'll generate a bid / no-bid report based on default criteria. This may take a few minutes."
                />
                <Step
                  stepNo={4}
                  icon="fileCheck02"
                  description="Review & refine findings to reach a bid / no-bid decision."
                />
              </Flex>
            </Flex>
            <Flex width={600} gap={0} flexDirection="column">
              <Flex gap={16} flexDirection="column">
                <UploadDocumentsForm
                  control={control}
                  name="files"
                  uploadFileStatus={uploadStatus}
                  setUploadFileStatus={setUploadStatus}
                  isUploadingBid={isUploadingBid}
                />
              </Flex>
              <GenerateBtnContainer>
                {/* If somehow a user navigates here, yet shouldn't be here. A paywall modal shall halt their tresspass */}
                <CreditAccessWrapper
                  requiredDataType="BIDS"
                  creditType="BID"
                  contextSource="Bid studio"
                >
                  <Button
                    buttonType="primary"
                    disabled={files.length === 0 || opportunityLoading || isUploadingBid}
                    onClick={async () => {
                      await createBid({ bid: { procurementStageId, opportunityId } });
                    }}
                  >
                    {isUploadingBid && <LoadingOutlined />}Generate report
                  </Button>
                </CreditAccessWrapper>
              </GenerateBtnContainer>
            </Flex>
          </Container>
        </form>
      </DetailsContent>
    </DetailsPage>
  );
}

const BreadcrumbContainer = styled(Flex)(({ theme }) => ({
  justifyContent: "space-between",
  padding: 24,
  alignItems: "center",
  gap: "8px",
  borderBottom: `1px solid ${theme.colors.sysBorderDecorative}`,
}));

const Breadcrumb = styled(Flex)<{ inactive?: boolean }>(({ theme, inactive }) => ({
  alignItems: "center",
  gap: "4px",
  cursor: inactive ? "default" : "pointer",
  color: inactive ? theme.colors.sysTextSecondary : theme.colors.sysPrimaryDefault,
  "&:hover": {
    textDecoration: inactive ? "none" : "underline",
  },
}));

const Container = styled(Flex)({
  maxWidth: "1280px",
  width: "100%",
  justifyContent: "space-around",
  margin: "0 auto",
  alignItems: "flex-start",
  boxSizing: "border-box",
  paddingTop: "100px",
  gap: "40px",
});

const GenerateBtnContainer = styled.span(() => ({
  marginLeft: "auto",
  marginTop: "64px",
}));

type StepProps = {
  stepNo: number;
  icon: IconKey;
  description: string;
  // optional call to action to render at the bottom of the step
  // with the same spacing as the description
  cta?: ReactNode;
};
function Step({ stepNo, icon, description, cta }: StepProps) {
  const { colors } = useTheme();
  return (
    <Flex flexDirection="column" gap={8}>
      <Flex alignItems="center" gap={4}>
        <UIcon icon={icon} size={16} color={colors.sysTextSecondary} />
        <Text fontWeight="700" color={colors.sysTextSecondary}>
          Step {stepNo}
        </Text>
      </Flex>
      <Text color={colors.sysTextSecondary}>{description}</Text>
      {cta}
    </Flex>
  );
}

const StyledLinkText = styled(Text)(({ theme }) => ({
  cursor: "pointer",
  color: theme.colors.sysPrimaryDefault,

  ":hover": {
    textDecoration: "underline",
    color: theme.colors.sysPrimaryHover,
  },
}));
